import React, { useState } from "react";
import EditPlayerModal from "./EditPlayerModal";
import uploadToDropbox from "../../../services/dropboxService";
import { addPlayer, updatePlayer } from "../../../services/playerService";
import { SearchInput } from "../../admin";

function PlayersTab({
  players,
  clubs,
  loading,
  error,
  setError,
  setPlayers,
  handleEditPlayer,
  handleDeletePlayer,
}) {
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [search, setSearch] = useState("");
  const [clubFilter, setClubFilter] = useState("all");
  const [positionFilter, setPositionFilter] = useState("all");
  const [newPlayer, setNewPlayer] = useState({
    name: "",
    clubId: "",
    position: "",
    age: "",
    bio: "",
    preferredLeg: "",
    profileImageUrl: "",
    profileImageFile: null,
  });

  const handleSubmit = async (e, player) => {
    e.preventDefault();
    try {
      // Handle image upload first if there's a file
      let imageUrl = player.profileImageUrl;
      if (player.profileImageFile) {
        imageUrl = await uploadToDropbox(
          player.profileImageFile,
          `players/${Date.now()}_${player.profileImageFile.name}`
        );
      }

      if (editingPlayer) {
        const updatedPlayer = {
          ...player,
          playerId: editingPlayer.playerId,
          age: parseInt(player.age),
          clubId: player.clubId ? parseInt(player.clubId) : null,
          preferredLeg: player.preferredLeg || null,
          profileImageUrl: imageUrl,
        };

        const savedPlayer = await updatePlayer(updatedPlayer.playerId, updatedPlayer);
        setPlayers(
          players.map((p) =>
            p.playerId === savedPlayer.playerId ? savedPlayer : p
          )
        );
        setEditingPlayer(null);
      } else {
        const playerWithImage = {
          ...player,
          age: parseInt(player.age),
          clubId: player.clubId ? parseInt(player.clubId) : null,
          profileImageUrl: imageUrl,
        };
        const newPlayer = await addPlayer(playerWithImage);
        setPlayers([...players, newPlayer]);
        setNewPlayer({
          name: "",
          clubId: "",
          position: "",
          age: "",
          bio: "",
          preferredLeg: "",
          profileImageUrl: "",
          profileImageFile: null,
        });
        setShowAddModal(false);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const getClubName = (clubId) => {
    const club = clubs.find((c) => c.clubId === parseInt(clubId));
    return club ? club.name : "No Club";
  };

  /* Derived: search + filters */
  const positions = [...new Set(players.map((p) => p.position).filter(Boolean))];
  const filtered = players.filter((player) => {
    if (clubFilter !== "all" && String(player.clubId) !== String(clubFilter)) return false;
    if (positionFilter !== "all" && player.position !== positionFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      player.name?.toLowerCase().includes(q) ||
      getClubName(player.clubId)?.toLowerCase().includes(q) ||
      player.position?.toLowerCase().includes(q) ||
      player.bio?.toLowerCase().includes(q)
    );
  });

  if (loading) return <div className="text-gray-400 p-4">Loading players…</div>;
  if (error) return <div className="text-accent-soft p-4">Error: {error}</div>;

  return (
    <div className="space-y-4">
      {/* Toolbar: search + filters + add */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search players by name, club or bio…"
        />
        <select
          value={clubFilter}
          onChange={(e) => setClubFilter(e.target.value)}
          className="field !w-auto shrink-0"
        >
          <option value="all">All clubs</option>
          {clubs.map((club) => (
            <option key={club.clubId} value={club.clubId}>
              {club.name}
            </option>
          ))}
        </select>
        <select
          value={positionFilter}
          onChange={(e) => setPositionFilter(e.target.value)}
          className="field !w-auto shrink-0"
        >
          <option value="all">All positions</option>
          {positions.map((pos) => (
            <option key={pos} value={pos}>
              {pos}
            </option>
          ))}
        </select>
        {(search || clubFilter !== "all" || positionFilter !== "all") && (
          <button
            type="button"
            className="btn-ghost shrink-0"
            onClick={() => {
              setSearch("");
              setClubFilter("all");
              setPositionFilter("all");
            }}
          >
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="btn-primary shrink-0 lg:ml-auto"
        >
          Add Player
        </button>
      </div>

      <p className="text-xs text-gray-500">
        Showing {filtered.length} of {players.length} players
      </p>

      {/* Players Table */}
      <div className="card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-base font-semibold text-white">
              {search || clubFilter !== "all" || positionFilter !== "all"
                ? "No players match your filters"
                : "No players yet"}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {search || clubFilter !== "all" || positionFilter !== "all"
                ? "Try adjusting your search or filters."
                : "Add your first player to get started."}
            </p>
          </div>
        ) : (
        <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="table-head">
              <th className="px-3.5 py-2.5 text-left w-14"></th>
              <th className="px-3.5 py-2.5 text-left">Name</th>
              <th className="px-3.5 py-2.5 text-left">Club</th>
              <th className="px-3.5 py-2.5 text-left">Position</th>
              <th className="px-3.5 py-2.5 text-left">Age</th>
              <th className="px-3.5 py-2.5 text-left">Preferred leg</th>
              <th className="px-3.5 py-2.5 text-right sticky right-0 bg-surface-600 z-10 border-l border-line-strong">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((player) => (
              <tr key={player.playerId} className="table-row group">
                <td className="px-3.5 py-2.5">
                  {player.profileImageUrl ? (
                    <img
                      src={player.profileImageUrl}
                      alt={`${player.name}'s profile`}
                      className="w-9 h-9 rounded-full object-cover border border-line-strong"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-surface-700 border border-line-strong flex items-center justify-center text-xs font-bold text-accent-soft">
                      {player.name?.charAt(0)}
                    </div>
                  )}
                </td>
                <td className="px-3.5 py-2.5 text-white font-medium">{player.name}</td>
                <td className="px-3.5 py-2.5 text-gray-400">{getClubName(player.clubId)}</td>
                <td className="px-3.5 py-2.5">
                  {player.position ? (
                    <span className="badge-neutral">{player.position}</span>
                  ) : (
                    <span className="text-gray-600">—</span>
                  )}
                </td>
                <td className="px-3.5 py-2.5 text-gray-400 tabular-nums">{player.age || "—"}</td>
                <td className="px-3.5 py-2.5 text-gray-400">
                  {player.preferredLeg
                    ? player.preferredLeg.charAt(0).toUpperCase() + player.preferredLeg.slice(1)
                    : "—"}
                </td>
                <td className="px-3.5 py-2 sticky right-0 bg-surface-850 group-hover:bg-surface-700 border-b border-l border-line transition-colors">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingPlayer(player)}
                      className="btn-ghost !py-1.5 !px-3"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeletePlayer(player)}
                      className="btn-danger !py-1.5 !px-3"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        )}
      </div>

      {/* Add Player Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-surface-800 border border-line-strong rounded-xl w-full max-w-2xl">
            <h2 className="text-xl font-semibold mb-4">Add New Player</h2>

            <form
              onSubmit={(e) => handleSubmit(e, newPlayer)}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setNewPlayer({
                        ...newPlayer,
                        profileImageFile: e.target.files[0],
                      })
                    }
                    className="bg-surface-700 p-2 rounded w-full"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Name"
                  className="bg-surface-700 p-2 rounded"
                  value={newPlayer.name}
                  onChange={(e) =>
                    setNewPlayer({ ...newPlayer, name: e.target.value })
                  }
                />
                <select
                  className="bg-surface-700 p-2 rounded"
                  value={newPlayer.clubId}
                  onChange={(e) =>
                    setNewPlayer({ ...newPlayer, clubId: e.target.value })
                  }
                >
                  <option value="">Select Club</option>
                  {clubs.map((club) => (
                    <option key={club.clubId} value={club.clubId}>
                      {club.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Position"
                  className="bg-surface-700 p-2 rounded"
                  value={newPlayer.position}
                  onChange={(e) =>
                    setNewPlayer({ ...newPlayer, position: e.target.value })
                  }
                />
                <input
                  type="number"
                  placeholder="Age"
                  className="bg-surface-700 p-2 rounded"
                  value={newPlayer.age}
                  onChange={(e) =>
                    setNewPlayer({ ...newPlayer, age: e.target.value })
                  }
                />
                <select
                  className="bg-surface-700 p-2 rounded"
                  value={newPlayer.preferredLeg}
                  onChange={(e) =>
                    setNewPlayer({ ...newPlayer, preferredLeg: e.target.value })
                  }
                >
                  <option value="">Select Preferred Leg</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
                <textarea
                  placeholder="Bio"
                  className="bg-surface-700 p-2 rounded col-span-2"
                  value={newPlayer.bio}
                  onChange={(e) =>
                    setNewPlayer({ ...newPlayer, bio: e.target.value })
                  }
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="bg-surface-600 px-4 py-2 rounded hover:bg-surface-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-accent px-4 py-2 rounded hover:bg-accent-hover"
                >
                  Add Player
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingPlayer && (
        <EditPlayerModal
          player={editingPlayer}
          clubs={clubs}
          onSubmit={handleSubmit}
          onClose={() => setEditingPlayer(null)}
        />
      )}
    </div>
  );
}

export default PlayersTab;
